/*
 * From https://www.redblobgames.com/x/2145-spaceship-flyer/
 * Copyright 2021 Red Blob Games <redblobgames@gmail.com>
 * License: Apache-2.0 <http://www.apache.org/licenses/LICENSE-2.0.html>
 */

import createREGL from "../../js/regl";
import { earcut } from "earcut";
import { vec4, mat3, mat4 } from "gl-matrix";

function debug(msg: string) { document.getElementById('debug').innerText = msg; }
const canvas = document.querySelector("canvas");

/* Simulation */

class ShipThruster {
    // Position on the ship hull
    u: number; v: number;

    // The F values are the direction ship goes. The thruster
    // actually fires in the opposite direction, but that direction
    // only is used for the visual effects.
    forceU: number; forceV: number;

    get torque(): number { return this.forceV * this.u - this.v * this.forceU; }

    // The visuals scale with the sqrt of the force, to reflect a
    // thruster being modeled as a cylinder
    get visualScale(): number { return 1 / Math.sqrt(this.fuel); }

    au(ship: Ship): number { return this.forceU / ship.mass; }
    av(ship: Ship): number { return this.forceV / ship.mass; }
    aθ(ship: Ship): number { return this.torque / ship.momentOfInertia; }

    // Not sure what to do with this yet…
    get fuel(): number { return Math.hypot(this.forceU, this.forceV); }

    constructor(u: number, v: number, forceU: number, forceV: number) {
        this.u = u; this.v = v;
        this.forceU = forceU; this.forceV = forceV;
    }
}


class Ship {
    x: number = 0;
    y: number = 0;
    facingX: number = 0;
    facingY: number = 1;
    vx: number = 0;
    vy: number = 0;
    vθ: number = 0;
    maxSpeed: number = 100;

    hullShapeVertices: number[];
    hullShapeTriangulation: number[];

    thrusters: ShipThruster[];
    flightPlan: number[];
    mass: number = 1e-9;
    momentOfInertia: number = 1e-9;

    constructor(hullShape: number[], thrusters: ShipThruster[]) {
        this.hullShapeVertices = hullShape;
        this.hullShapeTriangulation = earcut(hullShape);
        this.thrusters = thrusters;
        this.flightPlan = thrusters.map(_ => 0.0);
        this.updateMass();
    }

    updateMass() {
        this.mass = 100;
        this.momentOfInertia = 250;
    }

    applyFriction(dt: number) {
        let resistance = 0.06;
        let slowdown = 1.0 - dt * resistance;
        let fraction = 1.0 - dt * Math.hypot(this.vx, this.vy) / 100; // TODO: magic number
        this.vx *= slowdown * fraction;
        this.vy *= slowdown * fraction;
        this.vθ *= slowdown * (1.0 - Math.abs(this.vθ));
        // TODO: quadratic provides the maximum speed; linear I'm not
        // sure how useful it is; if I want it to come to a halt at
        // low speeds I need to add constant friction

        /* Math:
           
           Given some `acceleration`, velocity will increase by
           acceleration * dt each tick, and then get multiplied by
           slowdown * fraction. In the steady state, (1 - slowdown *
           fraction) * velocity = acceleration * dt. Solve:
           
           velocity * (1 - slowdown * fraction) = dt*acceleration
           velocity * (1 - (1 - dt*resistance) * (1 - dt*velocity/K)) = dt*acceleration
           velocity - (1 - dt*resistance) * (velocity - dt*velocity^2/K) = dt*acceleration
           velocity - velocity + dt*resistance * velocity + (1 - dt*resistance)*(dt/K * velocity^2) = dt*acceleration
           dt*resistance * velocity - (1 - dt*resistance)*(dt/K * velocity^2) = dt*acceleration
           resistance * velocity - (1 - dt*resistance)*(1/K * velocity^2) = acceleration
           (1 - dt*resistance)/K * velocity^2 + resistance * velocity - acceleration = 0
           
           Quadratic equation, with a = (1-dt*resistance)/K, b = resistance, c = -acceleration
           
           Solution: 2c / (-b ± sqrt(b² - 4ac))
           
           = -2*acceleration / (-resistance ± sqrt(resistance² + (1-dt*resistance)*4/K * acceleration))
           
           Well, that's not very enlightening. But for tuning, it means I now have a link between
           acceleration (one configurable parameter) and resistance (the other configurable parameter)
           and the maximum velocity.
           
           In addition to the steady state, I want to tune based on
           how quickly the ship accelerates to 90% of its maximum. To
           make the ship accelerate quicker, I need to increase
           acceleration and decrease K.
        */
    }


    calculateFlightPlan(desiredAu: number, desiredAv: number, desiredAθ: number) {
        let model = {
            optimize: 'action',
            opType: 'max',
            constraints: {} as any,
            variables: {} as any,
            options: {
                timeout: 10,
                tolerance: 0.05,
            }
        };

        // If we don't want motion along some axis, make sure it doesn't happen
        if (desiredAu === 0) model.constraints.au = { min: 0, max: 0 };
        if (desiredAv === 0) model.constraints.av = { min: 0, max: 0 };
        if (desiredAθ === 0) model.constraints.aθ = { min: 0, max: 0 };
        /*
          model.constraints.au = {min: -desiredAu, max: +desiredAu};
          model.constraints.av = {min: -desiredAv, max: +desiredAv};
          model.constraints.aθ = {min: -desiredAθ, max: +desiredAθ};
        */

        for (let i = 0; i < this.thrusters.length; i++) {
            let key = `thruster${i}`;

            // Physical limits on thruster
            model.constraints[key] = { min: 0, max: 1 };

            // Physics
            let au = -this.thrusters[i].au(this);
            let av = -this.thrusters[i].av(this);
            let aθ = -this.thrusters[i].aθ(this);
            model.variables[key] = {
                [key]: 1, // Copy input to output to apply constraints
                au,
                av,
                aθ,
                // TODO: angular motion and linear motion shouldn't be
                // added like this, but need a conversion factor
                action: desiredAu * au + desiredAv * av + desiredAθ * aθ,
            };
        }

        // I couldn't figure out how to make javascript-lp-solver work
        // with esbuild so I made it a global
        let results = globalThis.solver.Solve(model);
        // let scale = Math.max(1, Math.hypot(desiredAu, desiredAv, desiredAθ) / (1 + results.result));
        let scale = 1;
        this.flightPlan = this.thrusters.map((_, i) => scale * (results[`thruster${i}`] ?? 0));
    }

    applyThrusters(dt: number) {
        if (this.thrusters.length !== this.flightPlan.length) throw `Array length mismatch`;

        dt *= 10; // TODO: figure out what this means and what it should be

        let au = 0, av = 0, aθ = 0;
        for (let i = 0; i < this.thrusters.length; i++) {
            au += this.thrusters[i].au(this) * this.flightPlan[i];
            av += this.thrusters[i].av(this) * this.flightPlan[i];
            aθ += this.thrusters[i].aθ(this) * this.flightPlan[i];
        }

        // The thrusters point in the direction of the thrust and the
        // ship reacts by moving in the opposite direction
        this.vx -= dt * (au * this.facingY + av * this.facingX);
        this.vy -= dt * (-au * this.facingX + av * this.facingY);
        this.vθ -= dt * aθ;
        // TODO: decide which direction the thruster definition should
        // point, and make the physics and exhaust be opposite directions
    }
}

const player = new Ship(
    [1, -7, -1, -7, -2, 0, -5, 0, -5, 2,
        -2, 2.5, -2, 3, 2, 3, 2, 2.5, 5, 2, 5, 0, 2, 0],
    [
        // manually mirrored these
        new ShipThruster(-3,   +2.5,  0, -4),
        new ShipThruster(-1.5, -3.5, +1, +1),
        new ShipThruster(+3,   +2.5,  0, -4),
        new ShipThruster(+1.5, -3.5, -1, +1),
    ]
);


/* Blueprint */

function drawSpaceshipDesign() {
    let svg = document.getElementById('spaceship-design');
    let html = ``;
    html += `<defs><pattern id="svg-grid" width="1" height="1" patternUnits="userSpaceOnUse"><rect width="1" height="1" fill="hsl(232 36% 28%)" stroke="white" stroke-opacity="0.2" stroke-width="0.01"></pattern></defs>`;
    html += `<rect x="-10" y="-10" width="20" height="20" fill="url(#svg-grid)" />`;
    html += `<path d="M -10,0 l 20,0 M 0,-10 l 0,20" fill="none" stroke="white" stroke-width="0.01"/>`;
    html += `<polygon points="${player.hullShapeVertices}" fill="yellow" fill-opacity="0.2" stroke="white" stroke-width="0.1"/>`;
    for (let thruster of player.thrusters) {
        html += `<path transform="translate(${thruster.u},${thruster.v})" d="M 0,0 l${-thruster.forceU},${-thruster.forceV}" fill="none" stroke="cyan" stroke-width="0.2"/>`;
    }
    svg.innerHTML = html;
}

drawSpaceshipDesign();

/* Graphics */

const regl = createREGL({
    canvas,
    extensions: ['OES_standard_derivatives', 'angle_instanced_arrays'],
});

interface Uniforms {
    u_color: vec4;
    u_view: mat3;
    u_projection: mat4;
}

interface Attributes {
    a_position: number[];
}

interface Props {
    u_color: vec4;
    u_view: mat3;
    a_position: number[];
    elements: number[];
}

const drawShape = regl<Uniforms, Attributes, Props>({
    frag: `
precision mediump float;
uniform vec4 u_color;
void main () {
  gl_FragColor = u_color;
}`,
    
    vert: `
precision mediump float;
attribute vec2 a_position;
uniform mat3 u_view;
uniform mat4 u_projection;
varying vec3 v_color;
void main () {
gl_Position = u_projection * vec4(u_view * vec3(a_position, 1), 1);
}`,

    depth: { enable: false, func: 'less' },
    blend: { enable: true, func: { src: 'one', dst: 'one minus src alpha' }, },

    elements: regl.prop<Props, 'elements'>('elements'),
    attributes: {
        a_position: regl.prop<Props, 'a_position'>('a_position'),
    },
    uniforms: {
        u_color: regl.prop<Props, 'u_color'>('u_color'),
        u_view: regl.prop<Props, 'u_view'>('u_view'),
        u_projection: mat4.create(),
    },
});

let _drawPending = undefined;
function draw() {
    _drawPending = undefined;
    regl.clear({ color: [0, 0, 0, 1], depth: 1 });
    drawShape({
        u_color: [0.21, 0.25, 0.38, 1],
        u_view: mat3.create(),
        a_position: [-4, -4, +4, -4, 0, +4],
        elements: [0, 1, 2],
    });

    let transform = mat3.create();
    mat3.scale(transform, transform, [0.01, 0.01]);
    mat3.translate(transform, transform, [player.x, player.y]);
    mat3.rotate(transform, transform, Math.PI/2 + Math.atan2(player.facingY, player.facingX));
    // Thrusters drawn underneath the ship
    for (let i = 0; i < player.thrusters.length; i++) {
        let thruster = player.thrusters[i], level = player.flightPlan[i];
        let {u, v, forceU, forceV, visualScale} = thruster;
        let du = forceU * visualScale, dv = forceV * visualScale;
        let staticShape = [u - 0.3*dv, v + 0.3*du,
                           u + 0.3*dv, v - 0.3*du,
                           u + 0.2*dv - 1.3*du, v - 0.2*du - 1.3*dv,
                           u - 0.2*dv - 1.3*du, v + 0.2*du - 1.3*dv];
        let dynamicShape = [u - 1.3*du, v - 1.3*dv,
                            u - 1.3*du - 1*du*level - 0.8*dv*level,
                            v - 1.3*dv - 1*dv*level + 0.8*du*level,
                            u - 1.3*du - 4*du*level,
                            v - 1.3*dv - 4*dv*level,
                            u - 1.3*du - 1*du*level + 0.8*dv*level,
                            v - 1.3*dv - 1*dv*level - 0.8*du*level];
        drawShape({
            u_color: [0.5, 0.5, 0.5, 1],
            u_view: transform,
            a_position: staticShape,
            elements: earcut(staticShape),
        });
        drawShape({
            u_color: [0, 1, 1, 0.5],
            u_view: transform,
            a_position: dynamicShape,
            elements: earcut(dynamicShape),
        });
    }
    // Then the ship is drawn
    drawShape({
        u_color: [1, 0.8, 0, 1],
        u_view: transform,
        a_position: player.hullShapeVertices,
        elements: player.hullShapeTriangulation,
    });
}

/* Input */
let KeyboardTracker = {
    // On AZERTY, event.key will be 'w' but event.code will be 'KeyW'
    // When shifted, event.key will be 'W' but event.code will be 'KeyW'
    // I'm using code instead of key

    keysDown: {
        'KeyW': false,
        'KeyS': false,
        'KeyA': false,
        'KeyD': false,
        'KeyQ': false,
        'KeyE': false,
    },

    keyDown(event: KeyboardEvent) {
        if (this.keysDown[event.code] !== undefined) {
            this.keysDown[event.code] = true;
            event.preventDefault();
        }
    },

    keyUp(event: KeyboardEvent) {
        if (this.keysDown[event.code] !== undefined) {
            this.keysDown[event.code] = false;
            event.preventDefault();
        }
    },
}


let simulation = {
    running: undefined as number,
    lastTimestamp: null,

    start() {
        if (this.running) return;
        this.lastTimestamp = Date.now();
        this.running = setInterval(() => this.simulateStep(), 5);
    },

    stop() {
        if (!this.running) return;
        clearInterval(this.running);
        this.running = undefined;
        this.lastTimestamp = null;
    },

    simulateStep() {
        let currentTimestamp = Date.now();
        let dt = (currentTimestamp - this.lastTimestamp) * 1e-3;
        this.lastTimestamp = currentTimestamp;

        const { keysDown } = KeyboardTracker;
        let du = (keysDown.KeyQ ? -1 : 0) + (keysDown.KeyE ? +1 : 0);
        let dv = (keysDown.KeyW ? +1 : 0) + (keysDown.KeyS ? -1 : 0);
        let da = (keysDown.KeyA ? -1 : 0) + (keysDown.KeyD ? +1 : 0);

        player.applyFriction(dt);

        let { vx, vy, vθ } = player;
        let vu = player.facingY * vx - player.facingX * vy;
        let vv = -player.facingX * vx + player.facingY * vy;

        // TODO: magic numbers
        const acceleration = 25;
        let au = (du - vu / 50) * acceleration;
        let av = (dv - vv / 50) * acceleration;
        let aθ = (da - vθ / 50) * acceleration;

        player.calculateFlightPlan(au, av, aθ);
        if (du || dv || da) {
            // HACK: ignore the calculated thrusters and apply the movement directly
            player.vx += dt * (au * player.facingY + av * player.facingX);
            player.vy += dt * (-au * player.facingX + av * player.facingY);
            player.vθ += dt * aθ;
        } else {
            player.applyThrusters(dt);
        }

        const epsilon = 1e-6; // too high and velocity clamps to 0 when we're trying to accelerate past 0
        if (Math.abs(player.vx) < epsilon) player.vx = 0;
        if (Math.abs(player.vy) < epsilon) player.vy = 0;
        if (Math.abs(player.vθ) < epsilon) player.vθ = 0;

        if (player.vx === 0 && player.vy === 0 && player.vθ === 0) return;

        player.x += player.vx * dt;
        player.y += player.vy * dt;

        // TODO: magic number
        let cos = Math.cos(player.vθ * dt * 5), sin = Math.sin(player.vθ * dt * 5);
        let fX = player.facingX * cos + player.facingY * sin;
        let fY = -player.facingX * sin + player.facingY * cos;
        player.facingX = fX;
        player.facingY = fY;

        if (!_drawPending) { _drawPending = requestAnimationFrame(draw); }
    },
};


const instructions = document.getElementById('focus-instructions');
canvas.setAttribute('tabindex', "1");
canvas.addEventListener('keydown', event => KeyboardTracker.keyDown(event));
canvas.addEventListener('keyup', event => KeyboardTracker.keyUp(event));
canvas.addEventListener('blur', () => { simulation.stop(); instructions.classList.add('visible'); });
canvas.addEventListener('focus', () => { simulation.start(); instructions.classList.remove('visible'); })
draw();
canvas.focus();
simulation.start();



function renderThrusterConfigurations(id: string, ship: Ship, zeroAxis: string, ax: string, xLabels: string[], ay: string, yLabels: string[]) {
    const N = ship.thrusters.length;
    const scale = 100;
    const el = document.getElementById(id);
    let html = `
<g fill="none" stroke="hsl(0, 10%, 70%)" stroke-width="0.1">
<line x1="-9" x2="9"/>
<line y1="-9" y2="9"/>
</g>
<g font-size="1" font-family="sans-serif" text-anchor="middle">
<text x="-9.5" y="0.4">${xLabels[0]}</text>
<text x="9.5" y="0.4">${xLabels[1]}</text>
<text y="-9.2">${yLabels[0]}</text>
<text y="10">${yLabels[1]}</text>
</g>
`;
    html += `<g transform="scale(${scale})" fill="hsl(0, 50%, 50%)" stroke="white" stroke-width="${0.02 / scale}">`;
    for (let configuration = 0; configuration < (1 << N); configuration++) {
        let x = 0, y = 0;
        for (let t = 0; t < N; t++) {
            let thruster = ship.thrusters[t];
            let bit = ((1 << t) & configuration) ? 1 : 0;
            x += thruster[ax](ship) * bit;
            y += thruster[ay](ship) * bit;
        }
        html += `<circle r="${0.2 / scale}" cx="${x}" cy="${y}" />`;
    }
    html += `</g>`;
    el.innerHTML = html;
}
renderThrusterConfigurations('thruster-adws', player, 'θ', 'au', ['A', 'D'], 'av', ['W', 'S']);
renderThrusterConfigurations('thruster-qews', player, 'u', 'aθ', ['Q', 'E'], 'av', ['W', 'S']);
renderThrusterConfigurations('thruster-adqe', player, 'v', 'au', ['A', 'D'], 'aθ', ['Q', 'E']);
